import { useAppDispatch, useAppSelector } from "hooks";
import { selectDipState, setPricePoint, setTriggerOnCache } from "redux/slices";
import { Trigger } from "interfaces";
import { useEnvContext, useWebStoreContext } from "contexts";
import { FeaturesFlags } from "helpers";
import { featureTrackerAPI, offersApi } from "redux/apis";
import { FeatureType } from "interfaces/feature-tracker";
import { ClubGiftsGaloreResponse } from "interfaces/club-gifts-galore-v2";
import { orderBy } from "lodash";
import { useMemo } from "react";

interface SuppressDipsConfig {
	purchaseSuccess?: boolean;
	purchaseFailed?: boolean;
	purchaseCancelled?: boolean;
}

interface UseXsollaDipHandlersParams {
	pricePoint?: number;
	source?: string;
	triggerType?: string;
	items?: unknown[];
	priority?: number;
	suppressDips?: SuppressDipsConfig;
}

export const useXsollaDipHandlers = ({
	pricePoint,
	source,
	triggerType,
	items,
	priority,
	suppressDips
}: UseXsollaDipHandlersParams) => {
	const dispatch = useAppDispatch();
	const { data, currentDip } = useAppSelector(selectDipState);
	const { isFeatureFlagEnabled } = useEnvContext();
	const { authToken } = useWebStoreContext();

	const { data: clubGiftsGalore }: { data?: ClubGiftsGaloreResponse } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: FeatureType.CLUB_GIFT_BONUS_V2,
			isAuthenticated: !!authToken?.value
		});

	const { data: boxesData } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "clubGift",
		isAuthenticated: !!authToken?.value
	});

	const orderedOfferes = useMemo(() => {
		return orderBy(boxesData?.offers, ["priority"], ["asc"]);
	}, [boxesData]);

	const dispatchMultiReward = (trigger: Trigger, altTrigger: Trigger) => {
		if (data?.dips?.find((obj) => obj.dip.triggerType === trigger)) {
			dispatch(setTriggerOnCache(trigger));
		} else {
			dispatch(setTriggerOnCache(altTrigger));
		}
	};

	const handlePurchaseSuccessDips = () => {
		if (
			!suppressDips?.purchaseSuccess &&
			!currentDip?.dip.triggerType.includes("purchaseSuccess-")
		) {
			pricePoint && dispatch(setPricePoint(pricePoint));
			switch (source) {
				case "offer_card":
					dispatchMultiReward(
						`purchaseSuccess-${triggerType}` as Trigger,
						"purchaseSuccess"
					);
					break;

				case "waterfall":
					dispatchMultiReward(
						"purchaseSuccess-multi-reward",
						"purchaseSuccess"
					);
					break;

				case "endlessoffer":
					if (items && items.length > 1) {
						dispatchMultiReward(
							"purchaseSuccess-multi-reward",
							"purchaseSuccess"
						);
					} else {
						dispatchMultiReward(
							`purchaseSuccess-${triggerType}` as Trigger,
							"purchaseSuccess"
						);
					}
					break;

				case "gift_boxes":
					if (orderedOfferes?.[0]?.priority === priority) {
						const dipTrigger = data?.dips?.find(
							(obj) =>
								(obj.dip.triggerType === "clubGiftBonus-Contributed" &&
									isFeatureFlagEnabled(FeaturesFlags.CLUB_GIFT_BONUS) &&
									clubGiftsGalore?.tracker?.userClubTracker
										?.isUserQualifiedToContribute) ||
								obj.dip.triggerType === "clubShopPurchase"
						);
						if (dipTrigger) {
							dispatch(setTriggerOnCache(dipTrigger.dip.triggerType));
							return;
						}
					}
					dispatchMultiReward("purchaseSuccess-club-gifts", "purchaseSuccess");
					break;

				default:
					dispatchMultiReward(
						"purchaseSuccess-multi-reward",
						"purchaseSuccess"
					);
			}
		}
	};

	const handlePurchaseFailedDips = () => {
		if (!suppressDips?.purchaseFailed) {
			dispatch(setTriggerOnCache("purchaseFailed"));
		}
	};

	const handlePurchaseCancelledDips = () => {
		if (
			!suppressDips?.purchaseCancelled &&
			!currentDip?.dip.triggerType.includes("purchaseCancelled-")
		) {
			pricePoint && dispatch(setPricePoint(pricePoint));
			switch (source) {
				case "offer_card":
					dispatchMultiReward(
						`purchaseCancelled-${triggerType}` as Trigger,
						"purchaseCancelled"
					);
					break;
				case "gift_boxes":
					dispatchMultiReward(
						"purchaseCancelled-club-gifts",
						"purchaseCancelled"
					);
					break;

				case "endlessoffer":
					if (items && items.length > 1) {
						dispatchMultiReward(
							"purchaseCancelled-multi-reward",
							"purchaseCancelled"
						);
					} else {
						dispatchMultiReward(
							`purchaseCancelled-${triggerType}` as Trigger,
							"purchaseCancelled"
						);
					}
					break;

				default:
					dispatchMultiReward(
						"purchaseCancelled-multi-reward",
						"purchaseCancelled"
					);
			}
		}
	};

	return {
		handlePurchaseSuccessDips,
		handlePurchaseFailedDips,
		handlePurchaseCancelledDips
	};
};
